import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { FeesApi, FeeDemandRow } from '@/services/fees.api';
import { AcademicClassesApi, AcademicYearsApi } from '@/services/academic.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Input, Select } from '@/components/ui/Input';
import { DataTable, useDataTable } from '@/components/data-table';
import { useTerminology } from '@/hooks/useTerminology';
import { usePermissions } from '@/hooks/usePermissions';
import { CreateFeesCollectionModal } from './lib/QuickCreateModals';

const columnHelper = createColumnHelper<FeeDemandRow>();

function fmtMoney(n: number): string {
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(d: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function FeesDemandsPage() {
  const term = useTerminology();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can } = usePermissions();
  const canWrite = can('/fees', 'create');
  const canDelete = can('/fees', 'delete');
  const dt = useDataTable({ defaultLimit: 20, defaultSortBy: 'studentName', defaultSortOrder: 'asc' });

  const [yearId, setYearId] = useState('');
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<FeeDemandRow | null>(null);
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);

  const { data: defaultYear, isLoading: defaultYearLoading } = useQuery({
    queryKey: ['fees', 'default-year'],
    queryFn: FeesApi.defaultYear,
  });
  const { data: yearsPage } = useQuery({
    queryKey: ['academic-years', 'all-for-fees-selector'],
    queryFn: () => AcademicYearsApi.list({ limit: 100, sortBy: 'start_date', sortOrder: 'desc' }),
  });
  const years = yearsPage?.items ?? [];
  const effectiveYearId = yearId || defaultYear?.id || '';
  const hasAnyData = !!defaultYear;

  const { data: classesPage } = useQuery({
    queryKey: ['academic-classes', 'all-for-fees-selector'],
    queryFn: () => AcademicClassesApi.list({ limit: 100 }),
  });
  const classes = classesPage?.items ?? [];
  const sections = classes.find((c) => c.id === classId)?.sections ?? [];

  const listParams = {
    ...dt.params,
    academicYearId: effectiveYearId || undefined,
    classId: classId || undefined,
    sectionId: sectionId || undefined,
  };

  const { data: pageData, isFetching } = useQuery({
    queryKey: ['fees', 'demands', listParams],
    queryFn: () => FeesApi.demandsList(listParams),
    enabled: !defaultYearLoading,
    placeholderData: keepPreviousData,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => FeesApi.deleteDemand(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fees', 'demands'] });
      qc.invalidateQueries({ queryKey: ['fees', 'report'] });
      setConfirmDelete(null);
    },
  });

  const columns = [
    columnHelper.display({
      id: 'no',
      header: 'No.',
      enableSorting: false,
      cell: (info) => (
        <span className="text-slate-400">{dt.pagination.pageIndex * dt.pagination.pageSize + info.row.index + 1}</span>
      ),
    }),
    columnHelper.accessor('studentName', { header: 'Student Name' }),
    columnHelper.accessor('className', { header: 'Class' }),
    columnHelper.accessor('feeStructureName', { header: 'Fee' }),
    columnHelper.accessor('academicYearName', { header: 'Session Year' }),
    // Per-installment breakdown, matching the old system — no "Installment
    // Name" column here (that's Adjustment/Screen A only; manually-created
    // demands have no legacy installment_name to show).
    columnHelper.accessor('dueDate', { header: 'Date', cell: (ctx) => fmtDate(ctx.getValue()) }),
    columnHelper.accessor('actualAmount', { header: 'Actual', cell: (ctx) => fmtMoney(ctx.getValue()) }),
    columnHelper.accessor('adjustedAmount', { header: 'Paid', cell: (ctx) => fmtMoney(ctx.getValue()) }),
    columnHelper.accessor('pending', {
      header: 'Balance',
      cell: (ctx) => <Badge tone={ctx.getValue() <= 0 ? 'green' : 'amber'}>{fmtMoney(ctx.getValue())}</Badge>,
    }),
    columnHelper.accessor('discountAmount', {
      header: 'Discount',
      cell: (ctx) => (ctx.getValue() ? <span title={ctx.row.original.discountReason ?? undefined}>{fmtMoney(Math.abs(ctx.getValue()))}</span> : '—'),
    }),
    columnHelper.display({
      id: 'actions',
      header: '',
      cell: (ctx) => (
        <div className="flex items-center gap-1">
          {canWrite && (
            <button
              type="button"
              className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              title="Edit"
              onClick={() => navigate(`/fees/demands/${ctx.row.original.id}/edit`)}
            >
              <Pencil className="h-4 w-4" />
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-red-700"
              title="Delete"
              onClick={() => setConfirmDelete(ctx.row.original)}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      ),
    }),
  ];

  return (
    <>
      <PageHeader
        title="Fees Demands"
        description="Manually-created fee demands, one at a time, with an inline payment."
        actions={
          canWrite && (
            <div className="flex items-center gap-2">
              <button type="button" className="btn-secondary" onClick={() => setQuickCreateOpen(true)}>
                <Plus className="mr-1.5 h-4 w-4" /> Create Fees Collection
              </button>
              <button type="button" className="btn-primary" onClick={() => navigate('/fees/demands/new')}>
                <Plus className="mr-1.5 h-4 w-4" /> Add Demand
              </button>
            </div>
          )
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            value={dt.searchInput}
            onChange={(e) => dt.setSearchInput(e.target.value)}
            placeholder="Search by name or admission #"
            className="pl-9"
          />
        </div>
        <Select className="!w-40" value={classId} onChange={(e) => { setClassId(e.target.value); setSectionId(''); }}>
          <option value="">All {term.level}</option>
          {classes.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
        <Select className="!w-40" value={sectionId} onChange={(e) => setSectionId(e.target.value)} disabled={!classId}>
          <option value="">All {term.group}</option>
          {sections.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        <Select className="!w-44" value={yearId || effectiveYearId} onChange={(e) => setYearId(e.target.value)}>
          {years.map((y) => (
            <option key={y.id} value={y.id}>
              {y.name}
            </option>
          ))}
        </Select>
      </div>

      {!hasAnyData && !defaultYearLoading && !pageData?.items.length ? (
        <div className="card p-8 text-center text-slate-500">No manual demands yet.</div>
      ) : (
        <DataTable
          columns={columns}
          result={pageData}
          state={dt}
          loading={isFetching}
          emptyMessage="No manual demands for the selected filters."
          getRowId={(row) => row.id}
        />
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="card w-full max-w-sm p-5">
            <h3 className="font-semibold text-slate-900">Delete this demand?</h3>
            <p className="mt-1 text-sm text-slate-500">
              {confirmDelete.studentName} — {confirmDelete.feeStructureName} ({fmtMoney(confirmDelete.dmdAmount)}). This cannot be undone.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => setConfirmDelete(null)}>
                Cancel
              </button>
              <button
                className="btn-primary bg-red-600 hover:bg-red-700"
                disabled={deleteMutation.isPending}
                onClick={() => deleteMutation.mutate(confirmDelete.id)}
              >
                {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <CreateFeesCollectionModal
        open={quickCreateOpen}
        onClose={() => setQuickCreateOpen(false)}
        onCreated={() => {
          qc.invalidateQueries({ queryKey: ['fees', 'demands'] });
          qc.invalidateQueries({ queryKey: ['fees', 'report'] });
        }}
      />
    </>
  );
}
